
import React from 'react'
import {X, TrendingUp, TrendingDown, Package} from 'lucide-react'

interface HistoricoPrecosProps {
  isOpen: boolean
  onClose: () => void
  insumo?: any
  historico: any[]
}

export const HistoricoPrecos: React.FC<HistoricoPrecosProps> = ({
  isOpen,
  onClose,
  insumo,
  historico
}) => {
  if (!isOpen) return null

  // Se um insumo específico foi selecionado, filtrar apenas seu histórico
  // Caso contrário, mostrar histórico geral
  const historicoFiltrado = insumo 
    ? historico.filter(h => h?.insumoId === insumo._id)
    : historico

  const historicoOrdenado = historicoFiltrado
    .sort((a, b) => new Date(b?.dataAlteracao || 0).getTime() - new Date(a?.dataAlteracao || 0).getTime())

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getInsumoNome = (insumoId: string) => {
    // Esta função precisaria receber a lista de insumos como prop
    // Por enquanto, retornamos o ID
    return insumoId || 'Insumo não identificado'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {insumo ? `Histórico de Preços - ${insumo.nome}` : 'Histórico Geral de Preços'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {insumo && (
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h3 className="font-medium text-lg">{insumo.nome}</h3>
              <p className="text-gray-600">
                Preço atual: {formatCurrency(insumo.precoUnitario)} / {insumo.unidadeMedida}
              </p>
            </div>
          )}

          {historicoOrdenado.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p>
                {insumo 
                  ? 'Nenhuma alteração de preço registrada para este insumo.'
                  : 'Nenhuma alteração de preço registrada no sistema.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {historicoOrdenado.map((item, index) => (
                <div key={item._id || index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                        (item.percentualVariacao || 0) > 0 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {(item.percentualVariacao || 0) > 0 ? (
                          <TrendingUp size={14} />
                        ) : (
                          <TrendingDown size={14} />
                        )}
                        {(item.percentualVariacao || 0) > 0 ? '+' : ''}{(item.percentualVariacao || 0).toFixed(2)}%
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDate(item.dataAlteracao)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      por {item.usuario || 'Sistema'}
                    </span>
                  </div>

                  {!insumo && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-600">Insumo:</span>
                      <p className="font-medium">{getInsumoNome(item.insumoId)}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <span className="text-sm text-gray-600">Preço Anterior:</span>
                      <p className="font-medium">{formatCurrency(item.precoAnterior)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Preço Novo:</span>
                      <p className="font-medium">{formatCurrency(item.precoNovo)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Variação:</span>
                      <p className="font-medium">
                        {formatCurrency((item.precoNovo || 0) - (item.precoAnterior || 0))}
                      </p>
                    </div>
                  </div>

                  {item.motivo && (
                    <div>
                      <span className="text-sm text-gray-600">Motivo:</span>
                      <p className="text-sm mt-1 bg-gray-50 p-2 rounded">
                        {item.motivo}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t">
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

export default HistoricoPrecos
